import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Archive, ArchiveRestore, Clock, Megaphone, PenLine, Plus, Users } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  AdminPage,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import { useEmployeeOptions, useEmployees } from '@/features/employees/api';
import {
  audienceLabel,
  draftOf,
  emptyDraft,
  useAdminChannels,
  useChannelMutations,
  useChannelReach,
  type AdminChannel,
  type ChannelDraft,
} from './api';

const INPUT = 'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * ניהול ערוצים (PRD 5.3.1).
 *
 * הערוץ והקהל שלו נולדים יחד. הטופס הקודם יצר ערוץ בלי קהל ואז
 * הציג אזהרה שאף אחד לא רואה אותו, וזה היה המצב הרגיל ולא חריג.
 */
export function ChannelsAdminPage() {
  const canManage = usePerm('messages.channels');
  const { data: options } = useEmployeeOptions();
  const employees = useEmployees();
  const channels = useAdminChannels();
  const mutations = useChannelMutations();

  const [draft, setDraft] = useState<ChannelDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const channelIds = useMemo(() => (channels.data ?? []).map((c) => c.id), [channels.data]);
  const reach = useChannelReach(channelIds);

  if (!canManage) return <Navigate to="/admin" replace />;

  const activeEmployees = (employees.data ?? []).filter((employee) => employee.is_active);

  async function save(values: ChannelDraft) {
    setError(null);
    try {
      await mutations.save.mutateAsync(values);
      setDraft(null);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <AdminPage title="ערוצים" subtitle="מי מקבל מה, לפי סניף, תפקיד או שם">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {draft === null && (
        <div>
          <Button variant="primary" onClick={() => setDraft(emptyDraft())}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            ערוץ חדש
          </Button>
        </div>
      )}

      {draft !== null && (
        <ChannelForm
          draft={draft}
          branches={options?.branches ?? []}
          jobTitles={options?.jobTitles ?? []}
          employees={activeEmployees}
          isSaving={mutations.save.isPending}
          onChange={setDraft}
          onSubmit={() => void save(draft)}
          onCancel={() => {
            setDraft(null);
            setError(null);
          }}
        />
      )}

      {channels.isPending && <Skeleton className="h-24 w-full" />}
      {channels.isError && <ErrorState onRetry={() => void channels.refetch()} />}
      {channels.data?.length === 0 && (
        <EmptyState title="אין ערוצים" description="ערוץ ראשון יחליף את קבוצת הוואטסאפ." />
      )}

      <ul className="grid list-none gap-3 p-0">
        {(channels.data ?? []).map((channel) => (
          <ChannelCard
            key={channel.id}
            channel={channel}
            reach={reach.data?.[channel.id]}
            onEdit={() => {
              setError(null);
              setDraft(draftOf(channel));
            }}
            onToggleArchive={() =>
              void mutations.setArchived
                .mutateAsync({ id: channel.id, isArchived: !channel.is_archived })
                .catch((caught: unknown) => setError(errorMessage(caught)))
            }
          />
        ))}
      </ul>
    </AdminPage>
  );
}

type Option = { id: string; name: string };

function ChannelForm({
  draft,
  branches,
  jobTitles,
  employees,
  isSaving,
  onChange,
  onSubmit,
  onCancel,
}: {
  draft: ChannelDraft;
  branches: Option[];
  jobTitles: Option[];
  employees: { id: string; first_name: string; last_name: string }[];
  isSaving: boolean;
  onChange: (draft: ChannelDraft) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const [search, setSearch] = useState('');

  const set = <K extends keyof ChannelDraft>(key: K, value: ChannelDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const toggle = (key: 'branchIds' | 'jobTitleIds' | 'employeeIds', id: string) => {
    const current = draft[key];
    set(key, current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  const term = search.trim();
  const shownEmployees = employees.filter(
    (employee) =>
      term === '' ||
      `${employee.first_name} ${employee.last_name}`.includes(term) ||
      draft.employeeIds.includes(employee.id),
  );

  const hasAudience =
    draft.wholeNetwork ||
    draft.branchIds.length > 0 ||
    draft.jobTitleIds.length > 0 ||
    draft.employeeIds.length > 0;

  return (
    <Panel>
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <label htmlFor="channel-name" className="text-label font-medium">
              שם הערוץ *
            </label>
            <input
              id="channel-name"
              value={draft.name}
              onChange={(event) => set('name', event.target.value)}
              className={INPUT}
            />
          </div>

          <div className="grid gap-1">
            <label htmlFor="channel-desc" className="text-label font-medium">
              תיאור
            </label>
            <input
              id="channel-desc"
              value={draft.description}
              onChange={(event) => set('description', event.target.value)}
              className={INPUT}
            />
          </div>
        </div>

        <fieldset className="grid gap-2 rounded-md border border-line p-3">
          <legend className="px-1 text-label font-medium">מי בערוץ</legend>

          <label className="flex min-h-11 items-center gap-2 text-body">
            <input
              type="checkbox"
              checked={draft.wholeNetwork}
              onChange={(event) => set('wholeNetwork', event.target.checked)}
              className="size-5"
            />
            כל הרשת
          </label>

          {!draft.wholeNetwork && (
            <>
              <PickRow
                label="סניפים"
                hint="אפשר כמה. בלי בחירה: כל הסניפים."
                options={branches}
                selected={draft.branchIds}
                onToggle={(id) => toggle('branchIds', id)}
              />

              <PickRow
                label="תפקידים"
                hint="אפשר כמה. בלי בחירה: כל התפקידים."
                options={jobTitles}
                selected={draft.jobTitleIds}
                onToggle={(id) => toggle('jobTitleIds', id)}
              />

              <p className="m-0 text-body-sm text-muted">
                {describeRules(draft, branches, jobTitles)}
              </p>
            </>
          )}

          <div className="grid gap-2">
            <span className="text-label font-medium">אנשים לפי שם</span>
            <p className="m-0 text-body-sm text-muted">
              מצטרפים לערוץ גם אם אינם עונים לכללים. ככה בונים פורום מנהלי סניפים בלי להמציא
              תפקיד חדש.
            </p>
            <label htmlFor="channel-people" className="sr-only">
              חיפוש עובד
            </label>
            <input
              id="channel-people"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="חיפוש עובד"
              className={INPUT}
            />
            <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
              {shownEmployees.map((employee) => (
                <Chip
                  key={employee.id}
                  active={draft.employeeIds.includes(employee.id)}
                  onClick={() => toggle('employeeIds', employee.id)}
                >
                  {employee.first_name} {employee.last_name}
                </Chip>
              ))}
            </div>
          </div>

          {!hasAudience && (
            <p className="m-0 text-body-sm text-warn">
              עוד לא נבחר קהל. ערוץ בלי קהל לא נשמר, כי אף אחד לא יראה אותו.
            </p>
          )}
        </fieldset>

        <fieldset className="grid gap-2 rounded-md border border-line p-3">
          <legend className="px-1 text-label font-medium">איך הערוץ מתנהג</legend>

          <label className="flex min-h-11 items-center gap-2 text-body">
            <input
              type="checkbox"
              checked={draft.isAnnouncementOnly}
              onChange={(event) => set('isAnnouncementOnly', event.target.checked)}
              className="size-5"
            />
            ערוץ הודעות רשמיות
          </label>
          <p className="m-0 text-body-sm text-muted">
            כותבים בו רק בעלי ההרשאה "כתיבה בערוצי הודעות רשמיות". כולם קוראים.
          </p>

          <label className="flex min-h-11 items-center gap-2 text-body">
            <input
              type="checkbox"
              checked={draft.onShiftOnly}
              onChange={(event) => set('onShiftOnly', event.target.checked)}
              className="size-5"
            />
            רק מי שנמצא במשמרת עכשיו
          </label>
          <p className="m-0 text-body-sm text-muted">
            כל הודעה מגיעה רק למי שהיה במשמרת ברגע שנשלחה, ומתריעה לו מיד. מי שייכנס למשמרת אחר כך
            לא יראה אותה — היא שייכת לרגע שבו נכתבה.
          </p>
        </fieldset>

        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            variant="primary"
            disabled={isSaving || draft.name.trim() === '' || !hasAudience}
          >
            {draft.id === null ? 'יצירת הערוץ' : 'שמירת השינויים'}
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            ביטול
          </Button>
        </div>
      </form>
    </Panel>
  );
}

/** משפט אחד שאומר למי זה יגיע, לפני שלוחצים. */
function describeRules(draft: ChannelDraft, branches: Option[], jobTitles: Option[]): string {
  const names = (ids: string[], options: Option[]) =>
    options
      .filter((option) => ids.includes(option.id))
      .map((option) => option.name)
      .join(', ');

  if (draft.branchIds.length === 0 && draft.jobTitleIds.length === 0) {
    return 'בלי בחירה בשני הצירים לא נוצר כלל קהל, ורק האנשים שנבחרו בשמם יהיו בערוץ.';
  }

  const who =
    draft.jobTitleIds.length > 0 ? names(draft.jobTitleIds, jobTitles) : 'כל התפקידים';
  const where =
    draft.branchIds.length > 0
      ? `${draft.branchIds.length === 1 ? 'בסניף' : 'בסניפים'} ${names(draft.branchIds, branches)}`
      : 'בכל הסניפים';

  return `הכלל שייווצר: ${who}, ${where}.`;
}

function PickRow({
  label,
  hint,
  options,
  selected,
  onToggle,
}: {
  label: string;
  hint: string;
  options: Option[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <span className="text-label font-medium">{label}</span>
      <p className="m-0 text-body-sm text-muted">{hint}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <Chip key={option.id} active={selected.includes(option.id)} onClick={() => onToggle(option.id)}>
            {option.name}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'min-h-11 cursor-pointer rounded-pill border px-4 text-label font-medium',
        active ? 'border-olive bg-olive text-on-olive' : 'border-muted bg-transparent text-ink',
      )}
    >
      {children}
    </button>
  );
}

function ChannelCard({
  channel,
  reach,
  onEdit,
  onToggleArchive,
}: {
  channel: AdminChannel;
  reach: number | undefined;
  onEdit: () => void;
  onToggleArchive: () => void;
}) {
  return (
    <li className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-1">
          <span className="flex flex-wrap items-center gap-2 text-title font-medium">
            {channel.name}
            {channel.is_announcement_only && (
              <StatusBadge tone="good" icon={Megaphone}>
                רשמי
              </StatusBadge>
            )}
            {channel.on_shift_only && (
              <StatusBadge tone="warn" icon={Clock}>
                רק במשמרת
              </StatusBadge>
            )}
            {channel.is_archived && <StatusBadge tone="warn">בארכיון</StatusBadge>}
          </span>
          {channel.description && (
            <span className="text-body-sm text-muted">{channel.description}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!channel.is_archived && (
            <Link to={`/admin/channels/${channel.id}`} className="no-underline">
              <Button variant="secondary">
                <PenLine size={18} strokeWidth={1.75} aria-hidden="true" />
                כתיבת הודעה
              </Button>
            </Link>
          )}
          <Button variant="secondary" onClick={onEdit}>
            עריכת הערוץ
          </Button>
          <button
            type="button"
            aria-label={channel.is_archived ? 'החזרה מהארכיון' : 'העברה לארכיון'}
            title={channel.is_archived ? 'החזרה מהארכיון' : 'העברה לארכיון'}
            onClick={onToggleArchive}
            className="inline-flex size-11 cursor-pointer items-center justify-center rounded-sm bg-transparent text-ink"
          >
            {channel.is_archived ? (
              <ArchiveRestore size={20} strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <Archive size={20} strokeWidth={1.75} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <div className="grid gap-2">
        <span className="flex flex-wrap items-center gap-2 text-label font-medium">
          מי בערוץ
          {reach !== undefined && (
            <span className="inline-flex items-center gap-1 text-body-sm font-normal text-muted">
              <Users size={14} strokeWidth={1.75} aria-hidden="true" />
              {reach === 1 ? 'עובד אחד' : `${reach} עובדים`}
            </span>
          )}
        </span>

        <ul className="flex list-none flex-wrap gap-2 p-0">
          {channel.channel_audiences.map((audience) => (
            <li
              key={audience.id}
              className="rounded-pill border border-muted px-3 py-1 text-label"
            >
              {audienceLabel(audience)}
            </li>
          ))}
          {channel.channel_members_extra.map((person) => (
            <li
              key={person.employee_id}
              className="rounded-pill border border-olive px-3 py-1 text-label"
            >
              {person.employees
                ? `${person.employees.first_name} ${person.employees.last_name}`
                : 'עובד שנמחק'}
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}
